import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { useFormMethods } from '@hqo/multi-step-form';
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@hqo/ui-atoms';
import { useBuildingData } from 'store/building/hooks';
import type { CreateVisitFormV2Values } from '../../../../types/form-data.types';
import { useValidationMessage } from 'components/create-visit-form-v2/hooks/use-validation-message.hook';

interface EntryOptionProps {
  hidden: boolean;
}

export const EntryOption = ({ hidden }: EntryOptionProps): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const { passEntryOptions, defaultPassEntryOption } = useBuildingData();
  const form = useFormMethods() as unknown as UseFormReturn<CreateVisitFormV2Values>;
  const { control, setValue } = form;

  // Watched (not read via getValues) so the seed re-applies after the parent
  // form resets to building defaults, which wipes it back to ''.
  const visitEntryOptionValue = useWatch({ control, name: 'visitEntryOption' });
  useEffect(() => {
    if (hidden || !defaultPassEntryOption || !passEntryOptions?.includes(defaultPassEntryOption)) {
      return;
    }
    if (!visitEntryOptionValue) {
      setValue('visitEntryOption', defaultPassEntryOption, { shouldValidate: false });
    }
  }, [hidden, defaultPassEntryOption, passEntryOptions, visitEntryOptionValue, setValue]);

  if (hidden) {
    return null;
  }

  return (
    <div className="space-y-2" data-cy="entry-option">
      <Label htmlFor="visitEntryOption" className="gap-1">
        {formatMessage({
          id: 'createVisitFormV2.visitStep.entryOption',
          defaultMessage: 'Number of entries',
        })}
        <span className="text-destructive">*</span>
      </Label>
      <Controller
        name="visitEntryOption"
        control={control}
        // eslint-disable-next-line react/jsx-no-bind
        render={({ field, fieldState }) => (
          <>
            <Select value={field.value ?? ''} onValueChange={field.onChange}>
              <SelectTrigger id="visitEntryOption" className="w-full">
                <SelectValue
                  placeholder={formatMessage({
                    id: 'createVisitFormV2.visitStep.placeholders.entryOption',
                    defaultMessage: 'Select number of entries',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {passEntryOptions?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {formatMessage({ id: `labels.visitEntryOptions.${option}`, defaultMessage: option })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && (
              <p className="text-sm text-destructive">{formatValidationMessage(fieldState.error.message)}</p>
            )}
          </>
        )}
      />
    </div>
  );
};
